// FiscalDatePicker.js
import { useRef } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

/**
 * 会計日付選択コンポーネント
 *
 * Props:
 *
 * @param {Date} selected - 選択された日付
 * @param {function} onChange - 日付選択時に呼び出されるコールバック関数
 * @param {boolean} disabled - 入力を無効化するかどうか
 * @param {Date} minDate - 選択可能な最小日付
 * @param {Date} maxDate - 選択可能な最大日付
 * @param {string} className - 入力要素に適用するクラス名
 * @param {string} placeholderText - プレースホルダー
 * @param {function} onFocusOut - フォーカスアウト時のコールバック。未確定の手入力テキスト（なければnull）を渡す
 */
const FiscalDatePicker = ({
  selected,
  onChange,
  disabled,
  minDate,
  maxDate,
  className,
  placeholderText,
  onFocusOut
}) => {
  // 前回の月を保持するRef（無効な月選択時に自動修正するため）
  const lastMonthRef = useRef(null)
  // カレンダーに表示中の月を保持するRef
  const monthInCalendarRef = useRef(selected ? selected.getMonth() : new Date().getMonth())
  // 手入力中の未確定テキストを保持するRef（onChangeで確定されるとnullに戻す）
  const rawInputRef = useRef(null)

  /**
   * 手入力テキストを保持する（キー入力ごとに呼ばれる）
   *
   * @param {Object} event - 入力イベント
   */
  const handleChangeRaw = (event) => {
    if (event?.target instanceof HTMLInputElement) {
      rawInputRef.current = event.target.value
    }
  }

  /**
   * 日付確定時の処理。未確定テキストを破棄して親へ通知する
   *
   * @param {Date | null} date - 確定した日付
   * @param {Object} event - イベントオブジェクト
   */
  const handleChange = (date, event) => {
    rawInputRef.current = null
    onChange(date, event)
  }

  /**
   * フォーカスアウト（カレンダーを閉じる・blur）時に未確定テキストを親へ通知する
   *
   * カレンダー表示中のblurではreact-datepickerがonBlurを呼ばないため、 onCalendarCloseと合わせて両方のタイミングで呼び出す
   */
  const notifyFocusOut = () => {
    const rawText = rawInputRef.current
    rawInputRef.current = null
    if (onFocusOut) onFocusOut(rawText)
  }

  return (
    <DatePicker
      locale="ja" // 日本語ロケールを使用
      selected={selected} // 選択中の日付
      onChange={handleChange} // 日付変更時のコールバック
      onChangeRaw={handleChangeRaw} // 手入力テキストの保持
      onCalendarClose={notifyFocusOut} // カレンダーを閉じたタイミングで未確定入力を検証
      onBlur={notifyFocusOut} // カレンダー非表示時のフォーカスアウトを検証
      dateFormat="yyyy/MM/dd" // 日付表示フォーマット
      placeholderText={placeholderText} // プレースホルダー
      className={className}
      showMonthDropdown // 月ドロップダウン表示
      showYearDropdown // 年ドロップダウン表示
      dropdownMode="select" // ドロップダウンはselect形式
      disabled={disabled} // 入力無効化
      minDate={minDate} // 最小日付
      maxDate={maxDate} // 最大日付
      openToDate={minDate}
      // 日付セルのレンダリング。現在の月以外は薄い色で表示
      renderDayContents={(day, date) => {
        const isCurrentMonth = date.getMonth() === monthInCalendarRef.current
        return <span className={isCurrentMonth ? 'text-black' : 'text-gray-400'}>{day}</span>
      }}
      // カスタムヘッダーのレンダリング（年月選択と前後月ボタン）
      renderCustomHeader={({
        date,
        changeYear,
        changeMonth,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled
      }) => {
        const year = date.getFullYear()
        const month = date.getMonth()
        monthInCalendarRef.current = month // 現在のカレンダー月を更新

        // minDateからmaxDateまでの年リストを作成
        const years = []
        for (let y = minDate.getFullYear(); y <= maxDate.getFullYear(); y++) {
          years.push(y)
        }

        // 現在年に対して選択可能な月リストを作成
        let months = []
        if (year === minDate.getFullYear() && year === maxDate.getFullYear()) {
          for (let m = minDate.getMonth(); m <= maxDate.getMonth(); m++) months.push(m)
        } else if (year === minDate.getFullYear()) {
          for (let m = minDate.getMonth(); m < 12; m++) months.push(m)
        } else if (year === maxDate.getFullYear()) {
          for (let m = 0; m <= maxDate.getMonth(); m++) months.push(m)
        } else {
          months = [...Array(12).keys()] // 0〜11
        }

        // ヘッダーをレンダリング
        return (
          <div className="m-2 flex justify-center items-center gap-2">
            {/* 前月ボタン */}
            <button
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              className="text-sm px-2 py-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {'<<'}
            </button>

            {/* 年選択 */}
            <select
              value={year}
              onChange={(e) => changeYear(Number(e.target.value))}
              className="px-2 py-1 border font-bold rounded border-gray-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <span className="text-h7">年</span>

            {/* 月選択 */}
            <select
              value={month}
              onChange={(e) => changeMonth(Number(e.target.value))}
              className="px-2 py-1 font-bold border rounded border-gray-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              {months.map((m) => (
                <option key={m} value={m}>
                  {m + 1}
                </option>
              ))}
            </select>
            <span className="text-h7">月</span>

            {/* 翌月ボタン */}
            <button
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              className="text-sm px-2 py-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next month"
            >
              {'>>'}
            </button>
          </div>
        )
      }}
    />
  )
}

export default FiscalDatePicker
